/**
 * /accounting/gl/trial-balance — period-scoped TB grouped by account type.
 * Every type sub-totals; whole TB must balance (totalDR === totalCR).
 *
 * Surfaces a live "abnormal balances" advisory: any account whose net sits
 * on the wrong side of its normal balance (cash overdrawn, AR-credit, etc.)
 * gets flagged with a severity-coded reason.
 */
import { useMemo, useState } from "react";
import { useLogReadOnMount } from "@/hooks/useLogReadOnMount";
import { Link } from "react-router-dom";
import { AlertTriangle, Download } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import type { GLAccountType } from "@/data/accounting/glTypes";
import { getAbnormalBalances } from "@/data/accounting/reports/abnormalBalances";
import { exportCsv } from "@/lib/csvExport";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => n === 0 ? "—" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_ORDER: GLAccountType[] = ["asset", "liability", "equity", "revenue", "expense"];
const TYPE_LABEL: Record<GLAccountType, string> = {
  asset: "Assets", liability: "Liabilities", equity: "Equity", revenue: "Revenue", expense: "Expenses",
};

export default function TrialBalancePage() {
  useLogReadOnMount("trial-balance", { sensitivity: "default" });
  const { accounts, accountById, journalEntries, getTrialBalance } = useGeneralLedger();
  const periods = useMemo(() => {
    const s = new Set<string>();
    journalEntries.filter(j => j.status === "posted").forEach(j => s.add(j.period));
    return ["__all", ...Array.from(s).sort().reverse()];
  }, [journalEntries]);
  const [period, setPeriod] = useState<string>("__all");

  const balances = getTrialBalance(period === "__all" ? undefined : period)
    .filter(b => b.debit > 0 || b.credit > 0);

  const grouped = useMemo(() => {
    const by: Record<GLAccountType, typeof balances> = {
      asset: [], liability: [], equity: [], revenue: [], expense: [],
    };
    balances.forEach(b => {
      const a = accountById.get(b.accountId);
      if (a) by[a.type].push(b);
    });
    TYPE_ORDER.forEach(t => by[t].sort((x, y) => {
      const ax = accountById.get(x.accountId)!.number;
      const bx = accountById.get(y.accountId)!.number;
      return ax.localeCompare(bx);
    }));
    return by;
  }, [balances, accountById]);

  const totDr = balances.reduce((s, b) => s + b.debit, 0);
  const totCr = balances.reduce((s, b) => s + b.credit, 0);
  const inBalance = Math.round(totDr * 100) === Math.round(totCr * 100);

  const abnormal = useMemo(
    () => getAbnormalBalances(balances, accounts),
    [balances, accounts],
  );

  const downloadCsv = () => {
    exportCsv(
      `trial-balance-${period}.csv`,
      ["#", "Account", "Type", "Debit", "Credit", "Net"],
      balances.map(b => {
        const a = accountById.get(b.accountId)!;
        return [a.number, a.name, a.type, b.debit.toFixed(2), b.credit.toFixed(2), b.net.toFixed(2)];
      }),
    );
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 data-tour-id="trial-balance-table" className="text-2xl font-semibold tracking-tight">Trial Balance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every account's debit/credit totals for the period. Click an account to drill into GL detail.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-48">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {periods.map(p => (
                  <SelectItem key={p} value={p}>{p === "__all" ? "All periods" : p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={balances.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {abnormal.length > 0 && (
        <CockpitSection>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">
              <AlertTriangle className="h-4 w-4" />
              {abnormal.length} account{abnormal.length === 1 ? "" : "s"} with abnormal balance
            </div>
            <div className="space-y-1">
              {abnormal.map(r => (
                <div key={r.accountId} className="flex items-start gap-3 text-xs">
                  <Link to={`/accounting/gl/detail?account=${r.accountId}`} className="font-mono text-muted-foreground hover:text-foreground shrink-0 w-12">{r.accountNumber}</Link>
                  <span className="font-medium shrink-0 w-48 truncate">{r.accountName}</span>
                  <span className={
                    r.severity === "critical" ? "text-destructive shrink-0 w-24 tabular-nums" :
                    r.severity === "warn" ? "text-amber-600 dark:text-amber-500 shrink-0 w-24 tabular-nums" :
                    "text-muted-foreground shrink-0 w-24 tabular-nums"
                  }>
                    {fmt(r.amount)}
                  </span>
                  <span className="text-muted-foreground">{r.reason}</span>
                </div>
              ))}
            </div>
          </div>
        </CockpitSection>
      )}

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left w-24">#</th>
                <th className="px-3 py-2 text-left">Account</th>
                <th className="px-3 py-2 text-right w-32">Debit</th>
                <th className="px-3 py-2 text-right w-32">Credit</th>
              </tr>
            </thead>
            <tbody>
              {TYPE_ORDER.map(t => {
                const rows = grouped[t];
                if (!rows.length) return null;
                const tDr = rows.reduce((s, b) => s + b.debit, 0);
                const tCr = rows.reduce((s, b) => s + b.credit, 0);
                return (
                  <>
                    <tr key={`h-${t}`} className="bg-muted/20 border-t border-border">
                      <td colSpan={4} className="px-3 py-1.5 text-xs uppercase tracking-wide text-muted-foreground font-medium">
                        {TYPE_LABEL[t]}
                      </td>
                    </tr>
                    {rows.map(b => {
                      const a = accountById.get(b.accountId)!;
                      return (
                        <tr key={b.accountId} className="border-t border-border hover:bg-accent/30">
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{a.number}</td>
                          <td className="px-3 py-1.5">
                            <Link to={`/accounting/gl/detail?account=${a.id}`} className="hover:underline">
                              {a.name}
                            </Link>
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmt(b.debit)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmt(b.credit)}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-border bg-muted/10 text-xs">
                      <td colSpan={2} className="px-3 py-1.5 text-right text-muted-foreground">{TYPE_LABEL[t]} subtotal</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(tDr)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(tCr)}</td>
                    </tr>
                  </>
                );
              })}
              <tr className="border-t-2 border-border bg-muted/30">
                <td colSpan={2} className="px-3 py-2 text-right text-xs uppercase tracking-wide text-muted-foreground">Totals</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(totDr)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(totCr)}</td>
              </tr>
            </tbody>
          </table>
          <div className={`px-3 py-2 text-xs border-t border-border ${inBalance ? "text-muted-foreground" : "text-destructive"}`}>
            {inBalance
              ? "✓ Trial balance is in balance."
              : `⚠ Out of balance by $${Math.abs(totDr - totCr).toFixed(2)} — investigate journal entries.`}
          </div>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
